import { styled } from "../stitches.config";
import { useList, useRoom } from "@liveblocks/react";
import React, { useState, useEffect, useRef, useCallback } from "react";
import { useUserInfo, useUserInfos } from "../liveblocks/maps";
import { useAtom } from "jotai";
import {
  blocksOpsRefAtom,
  currentUserAtom,
  pastedTextContentAtom,
  sideNavOnAtom,
} from "../jotai/atoms";
import ReactTimeAgo from "react-time-ago";
import { colorValue } from "../css/color";
import Linkify from "linkify-react";
import useKeyAction from "../utils/useKeyAction";
import createBlock from "../blocks/createBlock";
import { pixelToGridPosition } from "../utils/gridPosition";
import { useUpdateAtom } from "jotai/utils";
import { StyledButton } from "../components/Button";
import Icon from "../components/Icon";
import useSound from "use-sound";
import { useSendAnalyticsEvent } from "../analytics/events";

type Message = {
  text: string;
  createdAt: number;
  madeBy: string;
};

const CHAT_DEFAULT_OPEN_HEIGHT = 320;

function ChatResizer({
  openHeight,
  setOpenHeight,
  setCollapsed,
}: {
  openHeight: number;
  setOpenHeight: (height: number) => void;
  setCollapsed: (collapsed: boolean) => void;
}) {
  const mouseYDownRef = useRef<number | null>(null);
  const heightDownRef = useRef(openHeight);

  const adjustHeight = (e: React.PointerEvent<HTMLDivElement>) => {
    if (mouseYDownRef.current === null) return;
    const newHeight = Math.min(
      window.innerHeight - 80,
      Math.max(120, heightDownRef.current - (e.clientY - mouseYDownRef.current))
    );
    setOpenHeight(newHeight);
  };

  return (
    <div
      style={{
        display: "flex",
        color: "#999",
      }}
    >
      <div
        style={{
          width: "100%",
          cursor: "ns-resize",
          userSelect: "none",
          display: "flex",
          justifyContent: "center",
          alignItems: "center",
          paddingLeft: 36,
          height: 24,
        }}
        onPointerDown={(e) => {
          e.stopPropagation();
          mouseYDownRef.current = e.clientY;
          heightDownRef.current = openHeight;
          (e.target as Element).setPointerCapture(e.pointerId);
        }}
        onPointerMove={(e) => {
          e.stopPropagation();
          adjustHeight(e);
        }}
        onPointerUp={(e) => {
          e.stopPropagation();
          mouseYDownRef.current = null;
          (e.target as Element).releasePointerCapture(e.pointerId);
        }}
      >
        <Icon icon="TwoBars" />
      </div>
      <StyledButton
        style={{ color: "#aaa" }}
        onClick={() => {
          setCollapsed(true);
        }}
      >
        <Icon icon="Chevron" />
      </StyledButton>
    </div>
  );
}

function Chat() {
  const room = useRoom();
  const messages = useList<Message>("chatMessages");
  const [playStamp] = useSound("/assets/sounds/chime.mp3", {
    volume: 0.2,
  });

  const [text, setText] = useState("");
  const [collapsed, setCollapsed] = useState(true);
  const [openHeight, setOpenHeight] = useState(CHAT_DEFAULT_OPEN_HEIGHT);
  const sendAnalyticsEvent = useSendAnalyticsEvent();

  const [userInfos] = useUserInfos();
  const [currentUser] = useAtom(currentUserAtom);
  const [currentUserInfo, currentUserClient] = useUserInfo(currentUser?.id);

  const [blocksOpsRef] = useAtom(blocksOpsRefAtom);
  const [sideNavOn] = useAtom(sideNavOnAtom);

  const inputRef = useRef<HTMLTextAreaElement | null>(null);
  const chatMessageRef = useRef<HTMLDivElement | null>(null);
  const messageContainerRef = useRef<HTMLDivElement | null>(null);

  const setPastedTextContent = useUpdateAtom(pastedTextContentAtom);
  const initialOpenCheck = useRef(false);
  const [inputFocused, setInputFocused] = useState(false);

  useKeyAction({ key: "/" }, (e) => {
    if (inputRef.current) {
      inputRef.current.focus();
      // preventDefault to avoid typing "/" on focus
      e.preventDefault();
    }
  });

  useKeyAction(
    { key: "Escape" },
    () => {
      if (inputRef.current) {
        inputRef.current.blur();
        setCollapsed(true);
      }
    },
    true
  );

  // Open chat if new messages since last visited
  useEffect(() => {
    if (
      messages &&
      messages.length > 0 &&
      currentUser &&
      currentUserInfo &&
      !initialOpenCheck.current
    ) {
      const lastMessage = messages.toArray()[messages.length - 1];
      if (
        currentUserInfo.lastMessageSeenAt === undefined ||
        lastMessage.createdAt > currentUserInfo.lastMessageSeenAt
      ) {
        setCollapsed(false);
        currentUserClient?.update({ lastMessageSeenAt: Date.now() });
      }
      initialOpenCheck.current = true;
    }
  }, [
    messages,
    currentUser,
    currentUserInfo,
    currentUserClient,
    initialOpenCheck,
  ]);

  const handleSend = () => {
    if (!messages || !currentUser) return;
    sendAnalyticsEvent({ name: "chatMessageSent" });
    const message: Message = {
      text: text,
      createdAt: Date.now(),
      madeBy: currentUser.id,
    };
    messages.push(message);
    setText("");
  };

  const scrollToBottom = useCallback(() => {
    if (!chatMessageRef.current) return;
    const scroller = chatMessageRef.current;
    if (scroller) {
      setTimeout(() => {
        scroller.scrollTop = scroller.scrollHeight;
      }, 0);
    }
  }, [chatMessageRef]);

  useEffect(() => {
    if (!messages) return;
    // Open and scroll to bottom if new messages
    const unsubscribe = room.subscribe(messages, () => {
      setCollapsed(false);
      scrollToBottom();
      currentUserClient?.update({ lastMessageSeenAt: Date.now() });

      if (document.hidden) {
        playStamp();
      }
    });
    return unsubscribe;
  }, [
    messages,
    room,
    scrollToBottom,
    setCollapsed,
    currentUserClient,
    playStamp,
  ]);

  const popBlock = (message: Message, messageElement: HTMLElement) => {
    if (!blocksOpsRef) return;
    const box = messageElement.getBoundingClientRect();
    const { upsertBlock } = blocksOpsRef.current;
    const newSize = {
      width: 16,
      height: 2,
    };
    const { x, y } = pixelToGridPosition({
      x: window.scrollX + box.x + box.width,
      y: window.scrollY + box.top,
    });
    const newBlock = createBlock(
      "text/json",
      {
        x: x + 2,
        y,
      },
      newSize
    );
    if (!upsertBlock || !newBlock) return;
    newBlock.madeBy = message.madeBy;
    upsertBlock(newBlock);
    // Use paste content so it gets autosized
    setPastedTextContent({
      id: newBlock.id,
      value: message.text,
    });
  };

  useEffect(() => {
    if (!collapsed) {
      scrollToBottom();
    }
  }, [collapsed, scrollToBottom]);

  return (
    <ChatContainer
      className="chat-container"
      onMouseDown={(e) => e.stopPropagation()}
      style={{
        height: collapsed ? "auto" : openHeight,
      }}
      withSidebar={sideNavOn}
    >
      {collapsed ? null : (
        <>
          <ChatResizer
            openHeight={openHeight}
            setOpenHeight={setOpenHeight}
            setCollapsed={setCollapsed}
          />
          <ChatMessages ref={chatMessageRef} className="chat-messages">
            <div ref={messageContainerRef}>
              {messages?.map((message, i) => {
                const user = userInfos[message.madeBy];
                return (
                  <ChatMessage
                    key={i}
                    className="chat-message"
                    style={{
                      color: user ? colorValue(user.color, 50) : "black",
                    }}
                  >
                    <div
                      style={{
                        display: "flex",
                        fontSize: "0.9rem",
                        marginBottom: 4,
                        alignItems: "baseline",
                        justifyContent: "space-between",
                      }}
                    >
                      <div style={{ display: "flex", alignItems: "baseline" }}>
                        {user && user.name ? (
                          <div
                            style={{ marginRight: "0.8ch", fontWeight: 700 }}
                          >
                            {user.name}
                          </div>
                        ) : null}
                        {message.createdAt && (
                          <div style={{ fontSize: "0.8rem" }}>
                            <ReactTimeAgo date={message.createdAt} />
                          </div>
                        )}
                      </div>
                      <StyledButton
                        style={{
                          color: user ? colorValue(user.color, 50) : "black",
                          padding: 0,
                          margin: 0,
                          height: 22,
                          display: "flex",
                          alignItems: "center",
                        }}
                        onClick={(e) => {
                          if (
                            e.target instanceof Element &&
                            e.target.parentElement
                          ) {
                            popBlock(message, e.target.parentElement);
                          }
                        }}
                      >
                        <Icon style={{ height: 16 }} icon="ArrowRightUp" />
                      </StyledButton>
                    </div>
                    <div style={{ wordBreak: "break-word" }}>
                      <Linkify options={{ target: "_blank" }}>
                        {message.text}
                      </Linkify>
                    </div>
                  </ChatMessage>
                );
              })}
              <div style={{ height: "1ch" }} />
            </div>
          </ChatMessages>
        </>
      )}
      <ChatInput>
        <textarea
          ref={inputRef}
          className="chat-input"
          value={text}
          style={{
            color: currentUser ? colorValue(currentUser.color, 50) : "black",
            outline:
              currentUser && inputFocused
                ? "solid 1px " + colorValue(currentUser.color, 60)
                : "none",
          }}
          onChange={(e) => setText(e.target.value)}
          onKeyDown={(e) => {
            if (e.key === "Enter" && !e.shiftKey) {
              e.preventDefault();
              handleSend();
            }
          }}
          onFocus={() => {
            setCollapsed(false);
            setInputFocused(true);
          }}
          onBlur={() => {
            setInputFocused(false);
          }}
          placeholder="Press / to message"
        />
      </ChatInput>
    </ChatContainer>
  );
}

export default React.memo(Chat);

const ChatContainer = styled("div", {
  position: "fixed",
  width: 380,
  background: "white",
  zIndex: "$chat",
  bottom: "$2",
  transform: "translateX(0)",
  transition: "transform 100ms ease-in-out",
  left: "$3",
  elevation: 2,
  display: "flex",
  flexDirection: "column",
  cursor: "auto",
  borderRadius: "$2",
  variants: {
    withSidebar: {
      true: {
        transform: `translateX($sizes$sideNavWidth)`,
      },
    },
  },
});

const ChatMessages = styled("div", {
  flex: 1,
  overflowY: "auto",
});

const ChatMessage = styled("div", {
  fontSize: "$5",
  padding: "$2 $3",
  whiteSpace: "pre-wrap",
  "& a": {
    color: "inherit",
  },
});

const ChatInput = styled("div", {
  position: "relative",
  display: "flex",
  flexDirection: "row",
  alignItems: "center",
  justifyContent: "space-between",
  "& textarea": {
    fontSize: "$5",
    boxSizing: "content-box",
    padding: "$2 $3",
    lineHeight: "1.4",
    height: "1.4em",
    resize: "none",
    minWidth: 0,
    width: "100%",
    borderBottomLeftRadius: "$2",
    borderBottomRightRadius: "$2",
  },
  "& textarea:focus": {
    outline: "none",
  },
});
